import { z } from 'zod';
import { ConversationRole, KeenOutcome, ModelRequestOutcome, TranscriptFragment } from './domain.js';

// The conversation events channel: GET /api/conversations/:id/events (text/event-stream).
// One live stream per open conversation view. Every viewer, including the poster of a message,
// sees the same events. Wire format: "event: <type>\ndata: <json>\n\n"; heartbeat ": ping".

/** A new turn row exists (a teammate's message, the poster's own turn, a streaming or summary turn). */
export const ConversationTurnEvent = z.object({
  turnId: z.string(),
  seq: z.number().int().positive(),
  kind: z.enum(['user', 'assistant', 'summary']),
  actorId: z.string(),
  /** Display name for a human actor; null for the assistant. */
  actorName: z.string().nullable(),
  /** The poster's dedupe key on a user turn, so optimistic turns reconcile. */
  clientTurnId: z.string().nullable(),
  mentions: z.array(z.string()),
  /** Keen mode: the decider's probability for a user turn; absent when no decision ran. */
  interjectP: z.number().min(0).max(1).optional(),
});
export type ConversationTurnEvent = z.infer<typeof ConversationTurnEvent>;

/** Keen mode decided on an already-stored user turn (the decision runs after the send, never blocking it). */
export const ConversationInterjectEvent = z.object({ turnId: z.string(), p: z.number().min(0).max(1) });
export type ConversationInterjectEvent = z.infer<typeof ConversationInterjectEvent>;
/** The persisted result of a keen decision, including silences with no probability. */
export const ConversationKeenOutcomeEvent = z.object({ turnId: z.string(), outcome: KeenOutcome, p: z.number().min(0).max(1).nullable() });

export const ConversationReasoningEvent = z.object({ assistantTurnId: z.string(), text: z.string() });
export const ConversationContentEvent = z.object({ assistantTurnId: z.string(), text: z.string() });
export const ConversationUsageEvent = z.object({
  assistantTurnId: z.string(),
  promptTokens: z.number().int().nonnegative().optional(),
  cachedPromptTokens: z.number().int().nonnegative().optional(),
  reasoningTokens: z.number().int().nonnegative().optional(),
  completionTokens: z.number().int().nonnegative().optional(),
  /** promptTokens over the conversation's budget; the web shows a banner from 0.7. */
  usedFraction: z.number().min(0).optional(),
  window: z.number().int().positive().optional(),
});
export const ConversationToolEvent = z.object({
  assistantTurnId: z.string(),
  callId: z.string(),
  name: z.string(),
  /** The query or the address, for display. */
  label: z.string(),
  status: z.enum(['running', 'done', 'error']),
  /** Short outcome for display: "8 results", "6,214 characters", or the error. */
  outcome: z.string().optional(),
});
export type ConversationToolEvent = z.infer<typeof ConversationToolEvent>;

export const SseFinishReason = z.enum(['stop', 'length', 'cancelled', 'error', 'unavailable', 'context_too_long']);
export type SseFinishReason = z.infer<typeof SseFinishReason>;
export const SseDoneStatus = z.enum([...ModelRequestOutcome.options, 'context_too_long']);
export type SseDoneStatus = z.infer<typeof SseDoneStatus>;
export const ConversationDoneEvent = z.object({
  assistantTurnId: z.string(),
  status: SseDoneStatus,
  finishReason: SseFinishReason.optional(),
  error: z.string().optional(),
});
export type ConversationDoneEvent = z.infer<typeof ConversationDoneEvent>;

/** Who is watching this conversation right now (one entry per connected person, deduped by user).
 *  Sent as a full snapshot whenever someone joins or leaves, and to a client just after it joins. */
export const ConversationPresenceEvent = z.object({
  members: z.array(z.object({ userId: z.string(), name: z.string().nullable() })),
});
export type ConversationPresenceEvent = z.infer<typeof ConversationPresenceEvent>;
export type ConversationReasoningEvent = z.infer<typeof ConversationReasoningEvent>;
export type ConversationContentEvent = z.infer<typeof ConversationContentEvent>;
export type ConversationUsageEvent = z.infer<typeof ConversationUsageEvent>;

// Payloads the generation pipeline emits; the events hub wraps them with the assistant turn id.
export const SseUsageEvent = ConversationUsageEvent.omit({ assistantTurnId: true });
export const SseToolEvent = ConversationToolEvent.omit({ assistantTurnId: true });
export type SseToolEvent = z.infer<typeof SseToolEvent>;
export type SseUsageEvent = z.infer<typeof SseUsageEvent>;

export const ConversationEvent = z.discriminatedUnion('event', [
  z.object({ event: z.literal('role_changed'), data: z.object({ role: ConversationRole, globalPromptVersion: z.number().int().positive() }) }),
  z.object({ event: z.literal('turn'), data: ConversationTurnEvent }),
  z.object({ event: z.literal('interject'), data: ConversationInterjectEvent }),
  z.object({ event: z.literal('keen_outcome'), data: ConversationKeenOutcomeEvent }),
  z.object({ event: z.literal('reasoning'), data: ConversationReasoningEvent }),
  z.object({ event: z.literal('content'), data: ConversationContentEvent }),
  z.object({ event: z.literal('usage'), data: ConversationUsageEvent }),
  z.object({ event: z.literal('tool'), data: ConversationToolEvent }),
  z.object({ event: z.literal('done'), data: ConversationDoneEvent }),
  z.object({ event: z.literal('presence'), data: ConversationPresenceEvent }),
]);
export type ConversationEvent = z.infer<typeof ConversationEvent>;

// The meeting events channel: GET /api/meetings/:id/events (docs/adr/OPEN-31). Segments arrive as
// they are appended; `ended` closes the live view.
export const MeetingSegmentEvent = z.object({
  fragment: TranscriptFragment.optional(),
  responseId: z.string().uuid().optional(),
  pieceIndex: z.number().int().nonnegative().optional(),
  index: z.number().int().nonnegative(),
  speaker: z.string(),
  /** The speaker's user id; absent for Jabberr. */
  userId: z.string().optional(),
  text: z.string(),
  start: z.number().nonnegative(),
  end: z.number().nonnegative(),
});
export type MeetingSegmentEvent = z.infer<typeof MeetingSegmentEvent>;
export const MeetingEndedEvent = z.object({ meetingId: z.string(), endedAt: z.string() });
export type MeetingEndedEvent = z.infer<typeof MeetingEndedEvent>;
export const MeetingEvent = z.discriminatedUnion('event', [
  z.object({ event: z.literal('segment'), data: MeetingSegmentEvent }),
  z.object({ event: z.literal('ended'), data: MeetingEndedEvent }),
]);
export type MeetingEvent = z.infer<typeof MeetingEvent>;

export const SSE_HEARTBEAT_MS = 15_000;
